import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { EnvironmentService } from '@patient-ui/shared-ui/utils';

import { LabLocation } from '../models';
import { isClosedToday } from '../utils/closed-today.utils';
import {
  ApiHours,
  ApiServiceSpecificHours,
  mapApiHours,
  mapApiServiceSpecificHours,
} from '../utils/hours-of-operation.utils';
import { isOpenNow } from '../utils/open-now.utils';

export interface FindALabSearchParams {
  serviceId: number;
  date: string;
  radius: number;
  latitude?: number;
  longitude?: number;
  address?: string;
  pageSize?: number;
  cursor?: string;
  openNow?: boolean;
  openOnWeekends?: boolean;
  isLabcorpAtWalgreens?: boolean;
}

export interface FindALabFavoritesSearchParams
  extends Omit<FindALabSearchParams, 'pageSize' | 'cursor'> {
  favoriteLocatorIds: number[];
}

export const FAL_DEFAULT_PAGE_SIZE = 50;

export interface FindALabPageInfo {
  pageSize: number;
  hasMore: boolean;
  nextCursor: string | null;
  maxResultsReached: boolean;
  message?: string;
}

export interface FindALabSearchPage {
  results: LabLocation[];
  pageInfo: FindALabPageInfo;
  startingCoordinate?: {
    latitude: number;
    longitude: number;
  };
  postalCode?: string;
}

export interface FindALabFavoritesPage {
  count: number;
  results: LabLocation[];
  startingCoordinate?: {
    latitude: number;
    longitude: number;
  };
  postalCode?: string;
}

@Injectable()
export class FindALabService {
  constructor(
    private http: HttpClient,
    private environmentService: EnvironmentService
  ) {}

  searchLabs(params: FindALabSearchParams): Observable<FindALabSearchPage> {
    const apiUrl = this.buildApiUrl(params);

    return this.http
      .get<unknown>(apiUrl, {
        headers: { Authorization: 'none' },
      })
      .pipe(map((data) => this.transformApiResponse(data, params)));
  }

  searchFavorites(
    params: FindALabFavoritesSearchParams
  ): Observable<FindALabFavoritesPage> {
    const apiUrl = this.buildFavoritesApiUrl(params);

    return this.http
      .get<unknown>(apiUrl, {
        headers: { Authorization: 'none' },
      })
      .pipe(map((data) => this.transformFavoritesResponse(data)));
  }

  private buildApiUrl(params: FindALabSearchParams): string {
    const { pageSize = FAL_DEFAULT_PAGE_SIZE, cursor } = params;
    const baseSearchParams = this.buildBaseSearchParams(params);
    baseSearchParams.set('pageSize', String(pageSize));

    if (cursor) {
      baseSearchParams.set('cursor', cursor);
    }

    const apiBaseUrl = `${this.environmentService.adminApiBaseUrl}/locations/fal/v2`;
    return this.buildLocationSearchUrl(apiBaseUrl, baseSearchParams, params);
  }

  private buildFavoritesApiUrl(params: FindALabFavoritesSearchParams): string {
    const baseSearchParams = this.buildBaseSearchParams(params);
    baseSearchParams.set(
      'favoriteLocatorIds',
      params.favoriteLocatorIds.join(',')
    );

    const apiBaseUrl = `${this.environmentService.adminApiBaseUrl}/locations/fal/favorites`;
    return this.buildLocationSearchUrl(apiBaseUrl, baseSearchParams, params);
  }

  private buildBaseSearchParams(params: FindALabSearchParams): URLSearchParams {
    return new URLSearchParams({
      client: 'LC',
      searchType: 'patient',
      radius: String(params.radius),
      serviceType: String(params.serviceId),
      date: params.date,
    });
  }

  private buildLocationSearchUrl(
    apiBaseUrl: string,
    baseSearchParams: URLSearchParams,
    params: FindALabSearchParams
  ): string {
    const { latitude, longitude, address } = params;
    const baseParams = baseSearchParams.toString();
    const optionalFilterParams = this.buildOptionalFilterParams(params);
    const queryParams = optionalFilterParams
      ? `${baseParams}&${optionalFilterParams}`
      : baseParams;

    const hasValidLatLon =
      latitude !== undefined &&
      latitude !== 0 &&
      longitude !== undefined &&
      longitude !== 0;

    if (hasValidLatLon) {
      return `${apiBaseUrl}/search?${queryParams}&lat=${latitude.toFixed(
        4
      )}&long=${longitude.toFixed(4)}`;
    } else if (address) {
      return `${apiBaseUrl}/searchByAddress?${queryParams}&address=${encodeURIComponent(
        address
      )}`;
    }

    throw new Error('Either lat/lon or address must be provided');
  }

  private buildOptionalFilterParams(params: FindALabSearchParams): string {
    const searchParams = new URLSearchParams();

    if (params.openNow) {
      searchParams.set('openNow', 'true');
    }

    if (params.openOnWeekends) {
      searchParams.set('openOnWeekends', 'true');
    }

    if (params.isLabcorpAtWalgreens) {
      searchParams.set('isLabcorpAtWalgreens', 'true');
    }

    return searchParams.toString();
  }

  private transformApiResponse(
    data: unknown,
    params: FindALabSearchParams
  ): FindALabSearchPage {
    const response = data as Record<string, unknown>;
    const locationsArray = response.results;
    const pageInfo = response.pageInfo as Record<string, unknown> | undefined;
    const startingCoordinate = response.startingCoordinate as
      | FindALabSearchPage['startingCoordinate']
      | undefined;

    return {
      results: Array.isArray(locationsArray)
        ? locationsArray.map((loc) =>
            this.transformLocation(loc as Record<string, unknown>)
          )
        : [],
      pageInfo: {
        pageSize:
          typeof pageInfo?.pageSize === 'number'
            ? pageInfo.pageSize
            : params.pageSize ?? FAL_DEFAULT_PAGE_SIZE,
        hasMore:
          typeof pageInfo?.hasMore === 'boolean' ? pageInfo.hasMore : false,
        nextCursor:
          typeof pageInfo?.nextCursor === 'string' ? pageInfo.nextCursor : null,
        maxResultsReached:
          typeof pageInfo?.maxResultsReached === 'boolean'
            ? pageInfo.maxResultsReached
            : false,
        message:
          typeof pageInfo?.message === 'string' && pageInfo.message.trim()
            ? pageInfo.message
            : undefined,
      },
      startingCoordinate,
      postalCode:
        typeof response.postalCode === 'string'
          ? response.postalCode
          : undefined,
    };
  }

  private transformFavoritesResponse(data: unknown): FindALabFavoritesPage {
    const response = data as Record<string, unknown>;
    const locationsArray = response.results;
    const results = Array.isArray(locationsArray)
      ? locationsArray.map((loc) =>
          this.transformLocation(loc as Record<string, unknown>)
        )
      : [];
    const startingCoordinate = response.startingCoordinate as
      | FindALabFavoritesPage['startingCoordinate']
      | undefined;

    return {
      count:
        typeof response.count === 'number' ? response.count : results.length,
      results,
      startingCoordinate,
      postalCode:
        typeof response.postalCode === 'string'
          ? response.postalCode
          : undefined,
    };
  }

  private transformLocation(loc: Record<string, unknown>): LabLocation {
    const today = this.getTodayLocalDate();
    const nowMs = Date.now();

    const closedTodayCalculated = isClosedToday(
      {
        closedToday: (loc.closedToday as boolean) || false,
        closingTodayAt: loc.closingTodayAt as string | undefined,
        timeZoneOffSet: (loc.timeZoneOffSet as string) || '-05:00',
      },
      today,
      nowMs
    );

    const openingSoonCalculated =
      loc.comingSoonIndicator && Boolean(loc.comingSoonDate);

    const isOpenNowCalculated =
      !closedTodayCalculated &&
      !openingSoonCalculated &&
      isOpenNow(
        {
          opensAt: loc.opensAt as string | undefined,
          closesAt: loc.closesAt as string | undefined,
          lunchStart: loc.lunchStart as string | undefined,
          lunchEnd: loc.lunchEnd as string | undefined,
          timeZoneOffSet: (loc.timeZoneOffSet as string) || '-05:00',
        },
        today,
        nowMs
      );

    const address = loc.address as Record<string, unknown> | undefined;

    return {
      id: loc.locatorId as number,
      name: loc.name as string,
      xcode: ((loc.vcode ?? loc.xcode) as string) || '',
      isWalgreens: (loc.isWalgreens as boolean) || false,
      isIOP:
        (loc.name as string)?.toLowerCase() !== 'labcorp' &&
        (loc.name as string)?.toLowerCase() !== 'labcorp at walgreens',
      address: {
        street:
          (address?.street as string) ||
          (address?.addressLine1 as string) ||
          '',
        addressLine1: (address?.addressLine1 as string) || '',
        city: (address?.city as string) || '',
        stateAbbr: (address?.stateAbbr as string) || '',
        postalCode: (address?.postalCode as string) || '',
        lat: (address?.lat as number) || 0,
        lng: (address?.lng as number) || 0,
      },
      position: {
        lat: (address?.lat as number) || 0,
        lng: (address?.lng as number) || 0,
      },
      distanceMiles: (loc.distanceFromStartingPoint as number) || 0,
      phone: (loc.phone as string) || '',
      fax: (loc.fax as string) || '',
      acceptsAppointments: (loc.canCreateAppointment as boolean) || false,
      services: ((loc.services as Array<Record<string, unknown>>) || []).map(
        (service) => ({
          id: service.id as number,
          shortName: service.shortName as string,
          appointmentOnlyService:
            (service.appointmentOnlyService as boolean) || false,
        })
      ),
      isOpenNow: isOpenNowCalculated,
      isOpenWeekends: (loc.openOnWeekends as boolean) || false,
      closedToday: closedTodayCalculated,
      isHoliday: (loc.isHoliday as boolean) || false,
      closingTodayTime: (loc.closingTodayAt as string) || null,
      openingSoon: openingSoonCalculated,
      openingDate: (loc.comingSoonDate as string) || null,
      isTemporarilyClosed: (loc.isTemporarilyClosed as boolean) || false,
      hoursOfOperation: (
        (loc.hoursofOperation as unknown as ApiHours[]) || []
      ).map(mapApiHours),
      lunchHoursOfOperation: (
        (loc.lunchHoursofOperation as unknown as ApiHours[]) || []
      ).map(mapApiHours),
      serviceSpecificHours: mapApiServiceSpecificHours(
        (loc.hoursofOperation as unknown as ApiHours[]) || [],
        loc.serviceSpecificHours as unknown as
          | ApiServiceSpecificHours[]
          | undefined
      ),
      appointmentOnlyDays: (loc.appointmentOnlyDays as string) || '',
      helperText: (loc.comment1 as string) || '',
      qrCodeUrl: loc.qrCodeSvg
        ? 'data:image/svg+xml,' + encodeURIComponent(loc.qrCodeSvg as string)
        : '',
      timeZoneOffset: (loc.timeZoneOffSet as string) || '-05:00',
      appointmentSlotsStatus:
        (loc.locationCardWithAppointmentSlotsStatus as string) ||
        'COMPLETE_VIEW_WITH_SLOTS',
    } as LabLocation;
  }

  private getTodayLocalDate(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
